import React, { useState, useEffect } from "react";
import { 
  ArrowLeft, Calendar, MapPin, Users, Globe, Instagram, 
  Facebook, Twitter, Bell, BellOff, Loader2, Share2
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OrganizerPage = ({ organizerId, onBack, onViewEvent, currentUser }) => {
  const [organizer, setOrganizer] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    if (organizerId) {
      fetchOrganizerData();
      if (currentUser) {
        checkSubscription();
      }
    }
  }, [organizerId, currentUser]);

  const fetchOrganizerData = async () => {
    setLoading(true);
    try {
      const [orgResponse, eventsResponse] = await Promise.all([
        axios.get(`${API}/organizer/${organizerId}/public`),
        axios.get(`${API}/organizer/${organizerId}/events`)
      ]);
      
      setOrganizer(orgResponse.data);
      setEvents(eventsResponse.data || []);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Organisateur non trouvé");
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const token = localStorage.getItem("tika_user_token");
      if (!token) return;
      
      const response = await axios.get(`${API}/subscriptions/check/${organizerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsSubscribed(response.data.subscribed);
    } catch (error) {
      console.log("Not subscribed");
    }
  };

  const handleSubscribe = async () => {
    if (!currentUser) {
      toast.error("Connectez-vous pour vous abonner");
      return;
    }

    setSubscribing(true);
    try {
      const token = localStorage.getItem("tika_user_token");
      
      if (isSubscribed) {
        await axios.delete(`${API}/unsubscribe/${organizerId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsSubscribed(false);
        setOrganizer(prev => ({ ...prev, followers_count: prev.followers_count - 1 }));
        toast.success("Désabonnement réussi");
      } else {
        await axios.post(`${API}/subscribe/${organizerId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsSubscribed(true);
        setOrganizer(prev => ({ ...prev, followers_count: prev.followers_count + 1 }));
        toast.success("Abonnement réussi ! Vous recevrez les nouveautés.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSubscribing(false);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/organizer/${organizerId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: organizer?.company_name || organizer?.name,
          text: `Découvrez les événements de ${organizer?.company_name || organizer?.name} sur TIKA !`,
          url
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          await navigator.clipboard.writeText(url);
          toast.success("Lien copié !");
        }
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié !");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!organizer) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <p className="text-muted-foreground mb-4">Organisateur non trouvé</p>
        <Button onClick={onBack}>Retour à l'accueil</Button>
      </div>
    );
  }

  const themeColor = organizer.theme_color || "#b3b3b3";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 sticky top-0 bg-background/80 backdrop-blur-xl z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <Button variant="outline" size="sm" onClick={handleShare}>
            <Share2 className="w-4 h-4 mr-2" />
            Partager
          </Button>
        </div>
      </header>

      {/* Banner */}
      <div 
        className="h-48 sm:h-64 relative"
        style={{ backgroundColor: themeColor + "40" }}
      >
        {organizer.banner && (
          <img 
            src={organizer.banner} 
            alt="" 
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
      </div>

      {/* Profile Section */}
      <div className="max-w-5xl mx-auto px-4 -mt-16 relative z-10">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          {/* Logo */}
          <div 
            className="w-28 h-28 rounded-xl overflow-hidden border-4 bg-background shadow-xl"
            style={{ borderColor: themeColor }}
          >
            {organizer.logo ? (
              <img src={organizer.logo} alt="" className="w-full h-full object-cover" />
            ) : (
              <div 
                className="w-full h-full flex items-center justify-center text-3xl font-bold text-white"
                style={{ backgroundColor: themeColor }}
              >
                {(organizer.company_name || organizer.name)?.[0]?.toUpperCase()}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 pb-2">
            <h1 className="text-2xl sm:text-3xl font-bold">
              {organizer.company_name || organizer.name}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {organizer.followers_count} abonnés
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {events.length} événements
              </span>
            </div>
          </div>

          {/* Subscribe Button */}
          <Button
            onClick={handleSubscribe}
            disabled={subscribing}
            className={isSubscribed ? "bg-muted hover:bg-muted/80" : ""}
            style={!isSubscribed ? { backgroundColor: themeColor } : {}}
          >
            {subscribing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isSubscribed ? (
              <>
                <BellOff className="w-4 h-4 mr-2" />
                Abonné
              </>
            ) : (
              <>
                <Bell className="w-4 h-4 mr-2" />
                S'abonner
              </>
            )}
          </Button>
        </div>

        {/* Description */}
        {organizer.description && (
          <p className="mt-6 text-muted-foreground max-w-2xl">
            {organizer.description}
          </p>
        )}

        {/* Social Links */}
        {(organizer.website || organizer.instagram || organizer.facebook || organizer.twitter) && (
          <div className="flex gap-3 mt-4 flex-wrap">
            {organizer.website && (
              <a 
                href={organizer.website.startsWith('http') ? organizer.website : `https://${organizer.website}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <Globe className="w-4 h-4" />
                Site web
              </a>
            )}
            {organizer.instagram && (
              <a 
                href={`https://instagram.com/${organizer.instagram.replace('@', '')}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-pink-500 hover:text-pink-400"
              >
                <Instagram className="w-4 h-4" />
                {organizer.instagram}
              </a>
            )}
            {organizer.facebook && (
              <a 
                href={`https://facebook.com/${organizer.facebook}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-400"
              >
                <Facebook className="w-4 h-4" />
                Facebook
              </a>
            )}
            {organizer.twitter && (
              <a 
                href={`https://twitter.com/${organizer.twitter.replace('@', '')}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-sky-500 hover:text-sky-400"
              >
                <Twitter className="w-4 h-4" />
                {organizer.twitter}
              </a>
            )}
          </div>
        )}
      </div>

      {/* Events Section */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-xl font-bold mb-6">Événements</h2>
        
        {events.length === 0 ? (
          <div className="text-center py-12 glass-card rounded-lg">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">Aucun événement pour le moment</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((event) => (
              <div 
                key={event.id}
                className="glass-card rounded-lg overflow-hidden cursor-pointer hover:border-primary/50 transition-all"
                onClick={() => onViewEvent(event.id)}
              >
                <div className="aspect-[4/3] relative">
                  <img 
                    src={event.image || "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae"} 
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 px-2 py-1 bg-black/70 rounded text-sm font-bold text-white">
                    {event.price}€
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold truncate">{event.title}</h3>
                  <p className="text-sm text-muted-foreground truncate">{event.artist}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <Calendar className="w-3 h-3" />
                    <span>{event.date}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{event.venue}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
